<template>
  <div>
    <Html :lang="head.htmlAttrs?.lang">
      <Head>
        <template v-if="!isDynamicSlugPage">
          <Link
            v-for="link in head.link"
            :id="link.id"
            :key="link.id"
            :rel="link.rel"
            :href="link.href"
            :hreflang="link.hreflang"
          />
        </template>
        <template v-for="meta in head.meta" :key="meta.id">
          <Meta :id="meta.id" :property="meta.property" :content="meta.content" />
        </template>
      </Head>
      <Body>
        <div class="fixed z-20 w-full">
          <LocalizationBar />
          <Header />
        </div>
        <UiAlertList />
        <main class="pb-16 pt-36 text-black">
          <slot />
        </main>
        <ReassuranceFooter />
        <Footer />
      </Body>
    </Html>
  </div>
</template>

<script setup lang="ts">
import { useAuthStore } from '~/stores/auth/authStore';
import { useCartStore } from '~/stores/cart/cartStore';
import { useCustomerStore } from '~/stores/customer/customerStore';
import { useWishlistStore } from '~/stores/wishlist/wishlistStore';
import { FetchState } from '~/utils/common';
import { StorageKey } from '~/utils/storage';

const { locale } = useI18n();
const route = useRoute();

const authStore = useAuthStore();
const customerStore = useCustomerStore();
const cartStore = useCartStore();
const wishlistStore = useWishlistStore();

const { isLoggedIn } = storeToRefs(authStore);

onMounted(() => {
  authStore.accessToken = getItemStorage<string>(StorageKey.ACCESS_TOKEN) ?? null;
});

watch(
  isLoggedIn,
  () => {
    let cartFetchNeeded = false;

    if (isLoggedIn.value) {
      customerStore.fetchCustomer();
      wishlistStore.fetchWishlist(locale.value as Locale);

      cartFetchNeeded = true;
    }

    const hasAnonymousCart = !!getItemStorage(StorageKey.CURRENT_CART);

    if (cartFetchNeeded || (!isLoggedIn.value && hasAnonymousCart)) {
      cartStore.fetchCart(locale.value as Locale);
    } else if (cartStore.cartFetchState === FetchState.INITIAL) cartStore.cartFetchState = FetchState.FINISHED;
  },
  { immediate: true },
);

const head = useLocaleHead({
  seo: true,
});

const isDynamicSlugPage = computed(() => (route.name as string)?.includes('slug___'));
</script>
